import { DownOutlined } from '@ant-design/icons';
import { useMemo, useRef } from 'react';

import useColor from '@/hooks/domain/useColor';
import useUserRole from '@/hooks/domain/useUserRole';
import useKeymap from '@/hooks/pure/useKeymap';
import useDropdownStore from '@/stores/pure/DropdownStore';

import { Flex } from '../base';
import SelectModal from './SelectModal';
import S from './styled';
import type { SelectDropdownProps } from './types';

const SelectDropdown = (props: SelectDropdownProps) => {
  const color = useColor();
  const { dropdownMapper, customMapper, dropdownStyle, ...rest } = props;
  const { currDropdown, shortcutEnabled, setCurrDropdown } = useDropdownStore(
    ({ currDropdown, shortcutEnabled, setCurrDropdown }) => ({
      currDropdown,
      shortcutEnabled,
      setCurrDropdown,
    }),
  );

  const dropdownOpen = useMemo(() => {
    return currDropdown === props.name;
  }, [currDropdown, props.name]);

  const { isEditor } = useUserRole();
  const ref = useRef<HTMLDivElement>(null);
  const setDropdownOpenHandler = () => {
    isEditor && setCurrDropdown(dropdownOpen ? null : props.name || '');
  };

  const onClick = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setDropdownOpenHandler();
  };

  useKeymap(props.shortcut, () => {
    shortcutEnabled && setDropdownOpenHandler();
  });

  return (
    <S.Style ref={ref} onClick={(e) => e.stopPropagation()}>
      {props.value !== null ? (
        customMapper ? (
          <Flex a="center" style={{ width: '100%' }}>
            <S.SelectBox
              isEditor={isEditor}
              j="space-between"
              a="center"
              onClick={(e) => onClick(e)}
              style={{ width: '100%' }}
            >
              {customMapper(props.value)}
            </S.SelectBox>
          </Flex>
        ) : (
          <Flex a="center" width="100%">
            <S.SelectedItemStyle
              isEditor={isEditor}
              a="center"
              j="space-between"
              onClick={(e) => onClick(e)}
              style={dropdownStyle}
            >
              <span>
                {dropdownMapper ? dropdownMapper(props.value) : props.value}
              </span>
              <DownOutlined style={{ fontSize: 14, color: color.gray600 }} />
            </S.SelectedItemStyle>
          </Flex>
        )
      ) : customMapper ? (
        <Flex a="center" style={{ width: '100%' }}>
          <S.SelectBox
            isEditor={isEditor}
            j="space-between"
            a="center"
            onClick={(e) => onClick(e)}
            style={{ width: '100%' }}
          >
            {customMapper(props.value)}
          </S.SelectBox>
        </Flex>
      ) : (
        <S.SelectedItemStyle
          isEditor={isEditor}
          a="center"
          j="space-between"
          onClick={(e) => onClick(e)}
          style={{ color: color.gray500, ...dropdownStyle }}
        >
          {props.placeholder}
          <DownOutlined style={{ fontSize: 14 }} />
        </S.SelectedItemStyle>
      )}

      <SelectModal
        {...rest}
        anchorRef={ref}
        modalOptions={{
          offset: {
            y: 8,
          },
        }}
      />
    </S.Style>
  );
};

export default SelectDropdown;
